import React, { useState } from 'react';
import styled from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import {
  PageWrapper,
  HeroSection,
  Container,
  PageTitle,
  PageSubtitle,
  PremiumCard,
  PrimaryButton,
  SecondaryButton,
  FormGroup,
  Label,
  Input,
  Textarea,
  Select,
  TwoColumnGrid,
  InfoBox,
  Section
} from '../styles/PremiumStyles';
import { theme } from '../styles/theme';

const FormCard = styled(PremiumCard)`
  max-width: 800px;
  margin: -50px auto 50px;
  background: white;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: -30px 15px 30px;
  }
`;

const ResponseCard = styled.div`
  background: linear-gradient(135deg, #10a37f 0%, #13c89f 100%);
  color: white;
  padding: 30px;
  border-radius: 20px;
  margin-top: 30px;
  box-shadow: 0 15px 40px rgba(16, 163, 127, 0.3);
  
  h3 {
    font-size: 24px;
    margin-bottom: 15px;
  }
  
  p {
    font-size: 18px;
    line-height: 1.6;
  }
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 215, 0, 0.2);
  border-top: 4px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 30px auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin: 60px 0;
`;

const FeatureCard = styled.div`
  text-align: center;
  padding: 30px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%);
  border-radius: 20px;
  border: 2px solid rgba(255, 215, 0, 0.2);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    border-color: rgba(255, 215, 0, 0.4);
  }

  .icon {
    font-size: 48px;
    margin-bottom: 20px;
  }

  h3 {
    font-size: 22px;
    color: #1a2332;
    margin-bottom: 15px;
    font-weight: 800;
  }

  p {
    font-size: 16px;
    color: #666;
    line-height: 1.6;
  }
`;

const SMSConsentWrapper = styled.div`
  margin-top: 20px;
  padding: 20px;
  background: rgba(255, 215, 0, 0.05);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 12px;
`;

const SMSConsentLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  line-height: 1.5;

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    cursor: pointer;
    accent-color: #ffd700;
    flex-shrink: 0;
  }
`;

export const QuickAnswer: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    annuityType: '',
    question: '',
    smsConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const [response, setResponse] = useState('');

  // Anti-spam fields
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Anti-spam validation
    if (honeypot) {
      console.warn('Spam detected: honeypot field filled');
      return;
    }

    const timeSpent = Date.now() - formLoadTime;
    if (timeSpent < 3000) {
      console.warn('Spam detected: form submitted too quickly');
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        annuity_type: formData.annuityType || 'Not provided',
        submission_type: 'quick_answer_page',
        form_source: 'quick_answer_page',
        sms_consent: formData.smsConsent ? 'Yes' : 'No',
        formLoadTime: formLoadTime,
        website: honeypot,
        message: `
QUICK ANSWER PAGE REQUEST
==========================

Your Name: ${formData.name}
Email Address: ${formData.email}
Phone Number: ${formData.phone || 'Not provided'}
SMS Consent: ${formData.smsConsent ? 'YES - Opted in to text messages' : 'NO - Did not opt in'}
Annuity Type: ${formData.annuityType || 'Not provided'}

Your Question:
${formData.question}

Submitted: ${new Date().toLocaleString('en-US', { timeZone: 'America/New_York', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true })}
        `.trim()
      };
      
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: emailData })
      });
      
      if (response.ok) {
        setIsSubmitting(false);
        setShowResponse(true);
        setResponse(`Thank you, ${formData.name}! Our expert team has received your question about ${formData.annuityType || 'annuities'}. We'll provide you with a detailed answer within 1-2 business days. Check your email at ${formData.email} for our response.`);
      } else {
        setIsSubmitting(false);
        alert('❌ Something went wrong. Please try again or call us at 800-922-1894.');
      }
    } catch (error) {
      setIsSubmitting(false);
      alert('❌ Something went wrong. Please try again or call us at 800-922-1894.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <SharedLayoutUltimate>
      <PageWrapper>
        <HeroSection style={{ color: 'white' }}>
          <Container>
            <PageTitle style={{ color: 'white' }}>
              Get <span className="gold">Quick Answers</span> to Your Annuity Questions
            </PageTitle>
            <PageSubtitle style={{ color: 'rgba(255, 255, 255, 0.95)' }}>
              Our expert team responds within 1-2 business days with personalized guidance
            </PageSubtitle>
          </Container>
        </HeroSection>
        
        <FormCard>
          <h2 style={{ 
            fontSize: '32px', 
            marginBottom: '30px', 
            color: '#1a2332',
            textAlign: 'center',
            fontWeight: '800'
          }}>
            Ask Your Question
          </h2>
          
          <InfoBox $variant="info">
            <h4>⚡ Quick Response Guarantee</h4>
            <p>We respond to all questions within 1-2 business days. For urgent matters, call us at 800-922-1894.</p>
          </InfoBox>
          
          <form onSubmit={handleSubmit}>
            <TwoColumnGrid>
              <FormGroup>
                <Label>
                  Your Name <span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>
                  Email Address <span className="required">*</span>
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
              </FormGroup>
            </TwoColumnGrid>
            
            <TwoColumnGrid>
              <FormGroup>
                <Label>
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Annuity Type
                </Label>
                <Select
                  name="annuityType"
                  value={formData.annuityType}
                  onChange={handleChange}
                >
                  <option value="">Select Type...</option>
                  <option value="fixed">Fixed Annuity</option>
                  <option value="variable">Variable Annuity</option>
                  <option value="indexed">Indexed Annuity</option>
                  <option value="immediate">Immediate Annuity</option>
                  <option value="deferred">Deferred Annuity</option>
                  <option value="not-sure">Not Sure</option>
                </Select>
              </FormGroup>
            </TwoColumnGrid>

            <SMSConsentWrapper>
              <SMSConsentLabel>
                <input
                  type="checkbox"
                  name="smsConsent"
                  checked={formData.smsConsent}
                  onChange={(e) => setFormData({ ...formData, smsConsent: e.target.checked })}
                />
                <span>
                  By providing your number, you are agreeing to receive text messages from Cap Averill II & Associates. This includes automated reminders, marketing messages, and general inquiries. Message frequency will vary. Msg & data rates may apply. Reply HELP for help or STOP to opt-out.
                </span>
              </SMSConsentLabel>
            </SMSConsentWrapper>

            <FormGroup>
              <Label>
                Your Question <span className="required">*</span>
              </Label>
              <Textarea
                name="question"
                value={formData.question}
                onChange={handleChange}
                placeholder="Please describe your annuity question or concern in detail..."
                required
                style={{ minHeight: '200px' }}
              />
            </FormGroup>

            {/* Honeypot field - hidden from users, catches bots */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
              <label htmlFor="website">Website</label>
              <input
                type="text"
                id="website"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>

            {!showResponse ? (
              <div style={{ textAlign: 'center', marginTop: '40px' }}>
                <PrimaryButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Get Your Answer'}
                </PrimaryButton>
              </div>
            ) : null}
            
            {isSubmitting && <LoadingSpinner />}
            
            {showResponse && (
              <ResponseCard>
                <h3>✅ Question Submitted Successfully!</h3>
                <p>{response}</p>
              </ResponseCard>
            )}
          </form>
        </FormCard>
        
        <Section>
          <Container>
            <h2 style={{ 
              fontSize: '42px', 
              textAlign: 'center', 
              marginBottom: '20px',
              color: '#1a2332',
              fontWeight: '800'
            }}>
              Why Choose Our Quick Answer Service?
            </h2>
            <p style={{ 
              fontSize: '20px', 
              textAlign: 'center', 
              color: '#666',
              marginBottom: '60px',
              maxWidth: '800px',
              margin: '0 auto 60px'
            }}>
              Get expert guidance without the sales pressure
            </p>
            
            <FeatureGrid>
              <FeatureCard>
                <div className="icon">⚡</div>
                <h3>Fast Response</h3>
                <p>We guarantee a personalized response within 1-2 business days</p>
              </FeatureCard>
              
              <FeatureCard>
                <div className="icon">🎯</div>
                <h3>Expert Analysis</h3>
                <p>40 years of experience analyzing every type of annuity</p>
              </FeatureCard>
              
              <FeatureCard>
                <div className="icon">🔒</div>
                <h3>100% Confidential</h3>
                <p>Your information is never shared or sold to third parties</p>
              </FeatureCard>
              
              <FeatureCard>
                <div className="icon">💰</div>
                <h3>Always Free</h3>
                <p>No fees, no obligations, no high-pressure sales tactics</p>
              </FeatureCard>
              
              <FeatureCard>
                <div className="icon">📊</div>
                <h3>Data-Driven</h3>
                <p>Answers backed by real market data, not sales pitches</p>
              </FeatureCard>
            </FeatureGrid>
            
            <InfoBox $variant="success" style={{ maxWidth: '800px', margin: '60px auto' }}>
              <h4>📞 Need Immediate Help?</h4>
              <p>
                If your question is urgent, don't wait! Call us directly at <strong>800-922-1894</strong> to speak with an annuity expert right away. We're here Monday-Friday, 9 AM - 5 PM EST.
              </p>
            </InfoBox>
          </Container>
        </Section>
      </PageWrapper>
    </SharedLayoutUltimate>
  );
};